import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const IndeeClipsVsYouTube: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('blog.backToBlog')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          Platform News • August 11, 2026
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          IndeeClips vs YouTube: What Makes Us Different
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          YouTube dominates global video, but IndeeClips is built differently—specifically for Indian creators and viewers. While we respect YouTube's innovation, we've designed features that address the unique needs of Bharat. Here's how IndeeClips stands apart and why thousands of Indian creators are making the switch.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Built for India, By Indians
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          IndeeClips isn't a YouTube clone—it's a platform designed around Indian internet behavior, languages, and content preferences from day one.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Hindi-first interface:</strong> Full platform available in Hindi and English, with more regional languages coming</li>
          <li><strong>India-optimized infrastructure:</strong> Servers located in India for faster loading, even on 3G/4G connections</li>
          <li><strong>Cultural relevance:</strong> Categories and recommendations tailored to Indian content—devotional, regional cinema, cricket, Bollywood</li>
          <li><strong>Data efficiency:</strong> Adaptive streaming works on slow connections without constant buffering</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Creator-First Philosophy
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          YouTube's algorithm can be opaque and frustrating. IndeeClips puts creators in control with transparent policies and better support.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Lower Barriers to Monetization
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          YouTube requires 1,000 subscribers and 4,000 watch hours. IndeeClips' monetization program (launching soon) will have significantly lower thresholds—because Indian creators shouldn't need massive audiences to earn.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Fair Revenue Split
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          YouTube keeps 45% of ad revenue. IndeeClips plans a more creator-friendly split, recognizing that without creators, there is no platform.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Direct Creator Support
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Try contacting YouTube support as a small creator—good luck. IndeeClips offers email support to all creators, with priority assistance for active uploaders.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          No Arbitrary Algorithm Changes
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          YouTube creators wake up to random algorithm shifts that tank their views overnight. IndeeClips commits to:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Algorithm transparency:</strong> Public documentation on how recommendations work</li>
          <li><strong>Advance notice:</strong> Major changes announced 30 days before implementation</li>
          <li><strong>Creator feedback:</strong> Algorithm updates tested with creator advisory board first</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Better Mobile Experience
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          70% of Indian users watch videos on mobile. IndeeClips mobile app is optimized for smaller screens and touch interactions:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Simpler navigation—fewer taps to reach content</li>
          <li>Gesture controls—double-tap to skip, swipe for volume/brightness</li>
          <li>Offline download support (coming soon) for watching without internet</li>
          <li>Lighter app size—under 50MB vs YouTube's 150MB+</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          What We're Still Building
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          We're honest about where we stand. YouTube has decades of development; IndeeClips is growing fast but still adding features:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Live streaming:</strong> Coming Q4 2026</li>
          <li><strong>Community posts:</strong> In development</li>
          <li><strong>Advanced analytics:</strong> Expanding creator dashboard features</li>
          <li><strong>Monetization:</strong> Launching creator program soon</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          The difference? We build based on Indian creator feedback, not Silicon Valley assumptions.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Why Creators Are Switching
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Thousands of Indian creators now cross-post to IndeeClips or have made it their primary platform. Here's why:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Less competition:</strong> Easier to stand out when you're not competing with MrBeast and Pewdiepie</li>
          <li><strong>Faster growth:</strong> Smaller platform means early adopters become big fish</li>
          <li><strong>Indian audience focus:</strong> Recommendations favor regional content over international viral trends</li>
          <li><strong>Platform loyalty:</strong> IndeeClips actively promotes Indian creators—we grow when you grow</li>
        </Box>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Join India's Video Revolution
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
            IndeeClips isn't trying to beat YouTube globally—we're building the best platform for Indian creators and viewers. If you're tired of being a small fish in a massive ocean, if you create content for Indian audiences, if you want a platform that actually listens to creators—IndeeClips is home.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Ready to grow with us? Start uploading today. Questions? Email{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
            .
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default IndeeClipsVsYouTube;
